import { Upload } from 'antd';
import { socket } from 'utils/socket';
import SendIcon from 'assets/icons/send';
import { useSelector } from 'react-redux';
import ImageIcon from 'assets/icons/image';
import { useTheme } from '@mui/material/styles';
import useFileUpload from 'hooks/useFileHandler';
import group from 'assets/images/users/group.svg';
import MemberList from 'sections/chat/MemberList';
import ChatHistory from 'project/chat/ChatHistory';
import { buildRoomId, toTrimmedString } from 'static-solutions/24155/console-solution/lead-workbench/utils/workbench';
import useIdentityCaseChatParticipants from './hooks/useIdentityCaseChatParticipants';
import useIdentityCaseChatMessages from './hooks/useIdentityCaseChatMessages';
import ReplyMessage from 'project/chat/ReplyMessage';
import AttachmentIcon from 'assets/icons/attachment';
import ChatContextWrapper from 'contexts/ChatContext';
import { CaretDownOutlined } from '@ant-design/icons';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';
import SelectedFilePreview from 'project/chat/SelectedFilePreview';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Fade, Stack, Paper, Modal, Popper, Backdrop, TextField, IconButton, ButtonBase, Typography, ClickAwayListener } from '@mui/material';

const colors = {
	bgBody: '#f3f4f6',
	bgSurface: '#ffffff',
	bgHeader: '#fcfcfc',
	textMain: '#0f172a',
	textMuted: '#64748b',
	textLight: '#94a3b8',
	border: '#e2e8f0',
	shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)',
};

const IdentityCaseChatModal = ({ open, item, onCancel, processCode, headerTitle }) => {
	const timer = useRef();
	const theme = useTheme();
	const anchorRef = useRef(null);
	const typingListener = useRef(0);
	const { uploadFile } = useFileUpload();
	// const [selectedUser, setSelectedUser] = useState(null);
	const [chatMessageText, setChatMessageText] = useState('');
	const [isSendingChatMessage, setIsSendingChatMessage] = useState(false);
	const chatMessagesContainerRef = useRef(null);

	const [file, setFile] = useState(null);
	const [image, setImage] = useState(null);
	const [status, setStatus] = useState([]);
	const [reply, setReply] = useState(null);
	const [fileRes, setFileRes] = useState(null);
	const [imageRes, setImageRes] = useState(null);
	const [actionType, setActionType] = useState(null);
	const [conversations, setConversations] = useState([]);
	const [participantsOpen, setParticipantsOpen] = useState(false);

	const accessToken = useSelector((state) => state?.auth?.accessToken);
	const companyCode = useSelector((state) => state?.profile?.profileData?.creatorOrganizationId);
	const userId = toTrimmedString(useSelector((state) => state?.profile?.profileData?.userId));

	const socketToken = accessToken || toTrimmedString(localStorage.getItem('accessToken'));

	const chatRoomId = useMemo(() => {
		const itemId = toTrimmedString(item?.id);
		const safeProcessCode = toTrimmedString(processCode);
		if (!itemId || !safeProcessCode) return '';
		return buildRoomId({ processCode: safeProcessCode, itemId });
	}, [item?.id, processCode]);

	const { participantsList, participantsMap } = useIdentityCaseChatParticipants({
		open,
		itemId: item?.id,
		processCode,
		userId,
	});

	const { chatMessages, isChatLoading, loadChatMessages } = useIdentityCaseChatMessages({
		open,
		itemId: item?.id,
		processCode,
	});

	const getMessageTimestamp = useCallback((message) => {
		if (message?.createdDate?.localDateTimeLong) return Number(message.createdDate.localDateTimeLong);
		if (message?.createdDate?.dateLong) return Number(message.createdDate.dateLong);
		if (message?.time?.localDateTimeLong) return Number(message.time.localDateTimeLong);
		if (message?.time?.dateLong) return Number(message.time.dateLong);

		const localDateTime = message?.createdDate?.localDateTime || message?.time?.localDateTime;
		const parsed = localDateTime ? new Date(localDateTime).getTime() : 0;
		return Number.isNaN(parsed) ? 0 : parsed;
	}, []);

	const normalizeConversation = useCallback((message) => {
		// console.log('Normalized Message:', message);

		const senderId = toTrimmedString(message?.senderId);
		const senderName = toTrimmedString(message?.senderName);

		return {
			...message,
			userId: senderId || message?.userId,
			senderId: senderId || message?.senderId,
			userFrom: message?.userFrom || {
				entityCode: senderId,
				entityFullName: senderName,
			},
			createdDate: message?.createdDate || message?.time,
			time: message?.time || message?.createdDate,
		};
	}, []);

	const normalizeConversationList = useCallback(
		(messages) => {
			const normalized = (Array.isArray(messages) ? messages : []).map(normalizeConversation);
			const sorted = normalized.sort((a, b) => getMessageTimestamp(b) - getMessageTimestamp(a));
			return sorted;
		},
		[getMessageTimestamp, normalizeConversation]
	);

	useEffect(() => {
		if (!open) return;
		setConversations(normalizeConversationList(chatMessages));
	}, [open, chatMessages, normalizeConversationList]);

	useEffect(() => {
		if (!open) return;
		setChatMessageText('');
		setReply(null);
		setFile(null);
		setFileRes(null);
		setImage(null);
		setImageRes(null);
		setConversations([]);
		setParticipantsOpen(false);
	}, [open, item?.id]);

	useEffect(() => {
		if (!open || !chatRoomId) return;

		const onIncomingMessage = (payload) => {
			const incomingMessage = payload?.data?.chat;
			const payloadRoomId = toTrimmedString(payload?.roomId) || toTrimmedString(incomingMessage?.roomId);
			if (!incomingMessage || payloadRoomId !== chatRoomId) return;

			const normalizedMessage = normalizeConversation(incomingMessage);
			const messageType = toTrimmedString(normalizedMessage?.type)?.toUpperCase();

			if (messageType === 'MESSAGE') {
				setConversations((prev) => {
					const merged = [normalizedMessage, ...prev];
					return Array.from(new Set(merged.map((item) => JSON.stringify(item)))).map((item) => JSON.parse(item));
				});
				setActionType('MESSAGE');
				return;
			}

			if (messageType === 'UPDATE' || messageType === 'DELETE') {
				setConversations((prev) => {
					const conversationClone = [...prev];
					const targetIndex = conversationClone.findIndex((msg) => msg?.id === normalizedMessage?.id);
					if (targetIndex > -1) {
						conversationClone.splice(targetIndex, 1, normalizedMessage);
					}
					return conversationClone;
				});
			}
		};

		socket.on('message', onIncomingMessage);
		return () => {
			socket.off('message', onIncomingMessage);
		};
	}, [open, chatRoomId, normalizeConversation]);

	useEffect(() => {
		if (actionType !== 'MESSAGE') return;
		const conversationContainer = document.getElementById('scrollableDiv');
		if (conversationContainer) {
			conversationContainer.scrollTop = conversationContainer.scrollHeight;
		}
		setActionType(null);
	}, [actionType, conversations]);

	useEffect(() => {
		if (!open || !companyCode || !userId || !chatRoomId || !participantsList?.length || !socketToken) return;
		socket.emit('chat_in', {
			userId,
			companyCode,
			instanceId: chatRoomId,
			token: socketToken,
			participantsList,
		});

		return () => {
			socket.emit('chat_out');
		};
	}, [open, companyCode, userId, chatRoomId, participantsList, socketToken]);

	useEffect(() => {
		const currentUser = { userId, status: 'ONLINE' };

		const onStatusUpdate = (data) => {
			setStatus([data, currentUser]);
		};

		const onInitialUsersStatus = (data) => {
			setStatus([...(Array.isArray(data) ? data : []), currentUser]);
		};

		socket.on('initial-users-status', onInitialUsersStatus);
		socket.on('presence-user', onStatusUpdate);
		socket.on('presence-chat', onStatusUpdate);
		socket.on('leave-chat', onStatusUpdate);
		socket.on('disconnect-chat-out', onStatusUpdate);
		socket.on('disconnect-user-out', onStatusUpdate);
		socket.on('logout-disconnection-user', onStatusUpdate);
		socket.on('logout-disconnection-chat', onStatusUpdate);
		socket.on('user-out', onStatusUpdate);
		socket.on('logout-disconnection', onStatusUpdate);

		return () => {
			socket.off('initial-users-status', onInitialUsersStatus);
			socket.off('presence-user', onStatusUpdate);
			socket.off('presence-chat', onStatusUpdate);
			socket.off('leave-chat', onStatusUpdate);
			socket.off('disconnect-chat-out', onStatusUpdate);
			socket.off('disconnect-user-out', onStatusUpdate);
			socket.off('logout-disconnection-user', onStatusUpdate);
			socket.off('logout-disconnection-chat', onStatusUpdate);
			socket.off('user-out', onStatusUpdate);
			socket.off('logout-disconnection', onStatusUpdate);
		};
	}, [userId]);

	useEffect(() => {
		if (!open) return;
		setParticipantsOpen(false);
	}, [open, item?.id]);

	const onChangeHandler = (event) => {
		setChatMessageText(event.target.value.length <= 1 ? event.target.value.trim() : event.target.value);
	};

	const onBlurHandler = () => {
		if (!userId || !companyCode || !chatRoomId || !socketToken) return;
		socket.emit('typing', { userId, companyCode, roomId: chatRoomId, token: socketToken, typing: 'false' });
	};

	const handleKeyDown = (event) => {
		window.clearTimeout(timer.current);

		if (typingListener.current === 0 && userId && companyCode && chatRoomId && socketToken) {
			socket.emit('typing', { userId, companyCode, roomId: chatRoomId, token: socketToken, typing: 'true' });
		}

		if (event.keyCode === 13 && event.shiftKey) {
			setChatMessageText(`${chatMessageText}\n`);
			event.preventDefault();
			return;
		}

		if (event?.key !== 'Enter') return;

		handleOnSend();
		onBlurHandler();
		event.preventDefault();
	};

	const handleKeyUp = () => {
		window.clearTimeout(timer.current);

		typingListener.current += 1;
		timer.current = window.setTimeout(() => {
			if (userId && companyCode && chatRoomId && socketToken) {
				socket.emit('typing', { userId, companyCode, roomId: chatRoomId, token: socketToken, typing: 'false' });
			}
			typingListener.current = 0;
		}, 1000);
	};

	const handleKeyPress = (event) => {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			handleOnSend();
		}
	};

	const uploadFileHandler = async (options, uploadType) => {
		const formData = new FormData();
		const api = '/upload-chat-file';

		formData.append('file', options?.file);
		formData.append('companyCode', companyCode);
		formData.append('userCode', userId);
		formData.append('roomId', chatRoomId);
		formData.append('context', 'DYNAMIC_GROUP_CHAT_FILE');

		const data = await uploadFile(options, formData, api);
		if (uploadType === 'file') setFileRes(data);
		if (uploadType === 'image') setImageRes(data);
	};

	const uploadProps = {
		name: 'file',
		multiple: false,
		showUploadList: false,
		headers: {
			token: `Bearer ${accessToken}`,
		},
		customRequest: (opt) => uploadFileHandler(opt, 'file'),
		onChange: (info) => setFile(info.file),
	};

	const uploadImageProps = {
		name: 'file',
		multiple: false,
		showUploadList: false,
		accept: '.jpg, .jpeg, .png',
		headers: {
			token: `Bearer ${accessToken}`,
		},
		customRequest: (opt) => uploadFileHandler(opt, 'image'),
		onChange: (info) => setImage(info.file),
	};

	const handleOnSend = () => {
		if (toTrimmedString(chatMessageText) === '' && !file && !image) return;
		if (!toTrimmedString(chatMessageText) && ((file && !fileRes) || (image && !imageRes))) return;

		handleSendChatMessage({
			message: chatMessageText,
			replyId: reply?.id,
			attachment: fileRes ? JSON.stringify(fileRes) : imageRes ? JSON.stringify(imageRes) : undefined,
			attachmentType: fileRes ? 'type/file' : imageRes ? 'type/image' : undefined,
		});
		setChatMessageText('');
		setReply(null);
		setFileRes(null);
		setFile(null);
		setImageRes(null);
		setImage(null);
	};

	const handleSendChatMessage = useCallback(
		async (options = {}) => {
			const safeMessage = toTrimmedString(options?.message ?? chatMessageText);
			const attachment = options?.attachment;
			const attachmentType = toTrimmedString(options?.attachmentType);
			const hasAttachment = Boolean(attachment && attachmentType);

			if ((!safeMessage && !hasAttachment) || !item?.id || !companyCode || !userId || !chatRoomId) return;

			try {
				setIsSendingChatMessage(true);
				const payload = {
					roomId: chatRoomId,
					processCode: toTrimmedString(processCode),
					itemId: item.id,
					context: 'dynamic-group-chat',
					userId,
					companyCode,
					message: safeMessage,
					type: 'MESSAGE',
					senderId: userId,
					participantIds: participantsList,
				};

				if (options?.replyId) payload.replyId = options.replyId;
				if (hasAttachment) {
					payload.attachment = attachment;
					payload.attachmentType = attachmentType;
				}

				socket.emit('message', payload);
				await new Promise((resolve) => setTimeout(resolve, 450));
				await loadChatMessages();
			} finally {
				setIsSendingChatMessage(false);
			}
		},
		[chatMessageText, item?.id, chatRoomId, participantsList, companyCode, userId, processCode, loadChatMessages]
	);

	// console.log('conversations', conversations);

	return (
		<Modal
			open={open}
			onClose={onCancel}
			sx={{ zIndex: 1000 }}
			closeAfterTransition
			slots={{ backdrop: Backdrop }}
			slotProps={{
				backdrop: {
					timeout: 200,
					sx: { backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' },
				},
			}}
		>
			<Fade in={open}>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: { xs: '95%', sm: '80%', md: '700px' },
						bgcolor: colors.bgSurface,
						borderRadius: '16px',
						boxShadow: colors.shadow,
						outline: 'none',
						display: 'flex',
						flexDirection: 'column',
						maxHeight: { xs: '95svh', md: '90svh' },
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							padding: '16px 24px',
							borderBottom: `1px solid ${colors.border}`,
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							bgcolor: colors.bgHeader,
							borderRadius: '16px 16px 0 0',
						}}
					>
						<Box>
							<Typography sx={{ fontSize: '12px', color: colors.textMuted, mb: '2px' }}>Chat</Typography>
							<Typography sx={{ fontSize: '16px', fontWeight: 700, color: colors.textMain }}>
								{toTrimmedString(headerTitle) || '-'}
							</Typography>
						</Box>

						<Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
							<ButtonBase
								sx={{
									p: 0.25,
									borderRadius: 1,
									'&:hover': { bgcolor: 'secondary.lighter' },
								}}
								aria-label="open participants"
								ref={anchorRef}
								aria-controls="participants-popover"
								aria-haspopup="true"
								onClick={() => setParticipantsOpen((prev) => !prev)}
							>
								<Stack direction="row" spacing={0.5} alignItems="center" sx={{ p: 0.5 }}>
									<img src={group} width={26} height={26} alt="participants" />
									<CaretDownOutlined />
								</Stack>
							</ButtonBase>

							<Box onClick={onCancel} sx={{ ml: 1, color: colors.textMuted, '&:hover': { color: colors.textMain } }}>
								<div style={{ fontSize: '24px', lineHeight: 1, cursor: 'pointer' }}>✕</div>
							</Box>
						</Box>
					</Box>

					<Popper
						id="participants-popover"
						placement="bottom-end"
						open={participantsOpen}
						anchorEl={anchorRef.current}
						role={undefined}
						transition
						disablePortal
						popperOptions={{
							modifiers: [
								{
									name: 'offset',
									options: {
										offset: [0, 9],
									},
								},
							],
						}}
						sx={{ zIndex: 1 }}
					>
						{({ TransitionProps }) => (
							<Transitions type="fade" in={participantsOpen} {...TransitionProps}>
								{participantsOpen && (
									<ClickAwayListener onClickAway={() => setParticipantsOpen(false)}>
										<Paper
											sx={{
												boxShadow: theme.customShadows?.z3 || '0 8px 24px rgba(15, 23, 42, 0.18)',
												width: 290,
												minWidth: 240,
												maxWidth: 290,
												[theme.breakpoints.down('md')]: {
													maxWidth: 250,
												},
											}}
										>
											<SimpleBar
												sx={{
													maxHeight: '300px',
													autoHide: false,
												}}
											>
												<MemberList participants={participantsMap} participantsStatus={status} isLoading={false} />
											</SimpleBar>
										</Paper>
									</ClickAwayListener>
								)}
							</Transitions>
						)}
					</Popper>

					<ChatContextWrapper
						reply={reply}
						roomId={chatRoomId}
						setReply={setReply}
						context="dynamic-group-chat"
						url={`/app/cc-sendMessage/${chatRoomId}`}
						conversations={conversations}
						setConversations={setConversations}
					>
						<Box
							ref={chatMessagesContainerRef}
							sx={{
								display: 'flex',
								flexDirection: 'column',
								height: '490px',
								bgcolor: colors.bgBody,
							}}
						>
							<ChatHistory conversations={conversations} hasMore={false} setPageNumber={() => {}} />

							{isChatLoading ? (
								<Box sx={{ order: 3, textAlign: 'center', py: 1 }}>
									<Typography sx={{ fontSize: '12px', color: colors.textMuted }}>Loading chat...</Typography>
								</Box>
							) : null}

							<TypingIndicator participants={participantsMap} userId={userId} />
							<ReplyMessage />

							<SelectedFilePreview
								file={file}
								setFile={setFile}
								setFileRes={setFileRes}
								image={image}
								setImage={setImage}
								setImageRes={setImageRes}
							/>

							<Box
								sx={{
									p: 1,
									bgcolor: '#f2f2f2',
									flex: 'none',
									order: 3,
									borderTop: '1px solid #e0e0e0',
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Box sx={{ display: 'flex', gap: 0.5 }}>
										<IconButton
											component="label"
											size="small"
											sx={{
												color: '#000',
												'& svg': {
													width: { xs: '20px', md: '24px' },
													height: { xs: '20px', md: '24px' },
												},
											}}
										>
											<Upload {...uploadProps}>
												<AttachmentIcon />
											</Upload>
										</IconButton>

										<IconButton
											component="label"
											size="small"
											sx={{
												color: '#000',
												'& svg': {
													width: { xs: '20px', md: '24px' },
													height: { xs: '20px', md: '24px' },
												},
											}}
										>
											<Upload {...uploadImageProps}>
												<ImageIcon />
											</Upload>
										</IconButton>
									</Box>

									<Box
										sx={{
											bgcolor: '#fff',
											flex: '1 1 auto',
											borderRadius: '24px',
											minHeight: '44px',
											display: 'flex',
											alignItems: 'center',
											px: 2,
											py: 0.5,
											boxShadow: '0px 1px 2px rgba(0,0,0,0.1)',
										}}
									>
										<TextField
											id="new_message"
											fullWidth
											multiline
											maxRows={4}
											placeholder="Type a message..."
											value={chatMessageText}
											onChange={onChangeHandler}
											onKeyDown={handleKeyDown}
											onKeyUp={handleKeyUp}
											onBlur={onBlurHandler}
											onKeyPress={handleKeyPress}
											variant="standard"
											InputProps={{
												disableUnderline: true,
												style: { fontSize: '15px', lineHeight: '1.5' },
											}}
											sx={{
												'& .MuiInputBase-root': { p: 0, m: 0 },
											}}
										/>
									</Box>

									<Box sx={{ pb: '2px', display: 'flex', flex: 'none' }}>
										<IconButton
											onClick={handleOnSend}
											disabled={isSendingChatMessage}
											sx={{
												bgcolor: '#4169E1',
												color: '#fff',
												borderRadius: '50%',
												width: { xs: '40px', md: '44px' },
												height: { xs: '40px', md: '44px' },
												transition: '0.2s',
												'&:hover': { bgcolor: '#3658b5' },
												'&.Mui-disabled': { bgcolor: '#94a3b8', color: '#e2e8f0' },
												boxShadow: { xs: 'none', md: 3 },
											}}
										>
											<SendIcon
												sx={{
													width: { xs: '18px', md: '20px' },
													height: { xs: '18px', md: '20px' },
													ml: '2px',
												}}
												color="#fff"
											/>
										</IconButton>
									</Box>
								</Box>
							</Box>
						</Box>
					</ChatContextWrapper>
				</Box>
			</Fade>
		</Modal>
	);
};

export default IdentityCaseChatModal;

const TypingIndicator = ({ participants, userId }) => {
	const [userTyping, setUserTyping] = useState({});

	useEffect(() => {
		const onTyping = (res) => {
			setUserTyping({ user: res?.data?.userId, isTyping: res?.data?.typing });
		};

		socket.on('typing', onTyping);
		return () => {
			socket.off('typing', onTyping);
		};
	}, []);

	if (userTyping?.isTyping === 'true' && userTyping?.user !== userId) {
		return (
			<Box sx={{ order: 3, px: 3, pb: 1 }}>
				<Stack direction="row" alignItems="center">
					<Typography sx={{ fontSize: '.75rem', mr: 0.5, color: 'gray' }}>
						{participants?.[userTyping?.user]?.entityFirstName ?? participants?.[userTyping?.user]?.entityFullName ?? 'Someone'} is typing
					</Typography>
					<div className="typing-indicator">
						<span></span>
						<span></span>
						<span></span>
					</div>
				</Stack>
			</Box>
		);
	}

	return null;
};
